import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { Formik, useFormikContext } from 'formik'
import * as Yup from 'yup'
import ConfirmModal from 'components/ConfirmModal'
import { _ } from 'utils'
import { CreateNavigationSubMenuMutation, UpdateNavigationSubMenuMutation } from 'generated/graphql'
import TextField from '../../forms/components/TextField'
import { showNotification } from '../../notifications'
import { showErrorNotification } from '../../notifications/notificationsSlice'
import { createNavigationSubMenu, updateNavigationSubMenu } from '../graphql/mutation'

interface CreateNavSubMenuProps {
    close: () => void
    onSubmitted: (data) => void
    menuId?: string
    newOrder?: number
    subMenu?: {
        id: number
        title: string
    }
}

interface CreateNavSubMenuModalProps {
    close: () => void
    isNew: boolean
}

const CreateValidation = Yup.object().shape({
    title: Yup.string().required().min(3).max(50),
})

const CreateNavSubMenuForm = () => (
    <>
        <TextField name="title" label={_('navigation.form.section_name')} isRequired />
    </>
)

const CreateNavSubMenuModal = ({ close, isNew }: CreateNavSubMenuModalProps) => {
    const { submitForm, isValid } = useFormikContext()
    return (
        <>
            <ConfirmModal
                close={close}
                title={isNew ? _('navigation.create_dialog.title') : _('navigation.edit_dialog.title')}
                confirmDescription={<CreateNavSubMenuForm />}
                confirmText={isNew ? _('navigation.form.create') : _('navigation.form.edit')}
                disabledConfirm={!isValid}
                onBeforeConfirm={submitForm}
                variantConfirm="success"
            />
        </>
    )
}

const NavSubMenuEditor = ({
    close,
    onSubmitted,
    newOrder,
    menuId,
    subMenu,
}: CreateNavSubMenuProps) => {
    const dispatch = useDispatch()

    const isNew = !subMenu
    const handleSubmit = useCallback(async ({ title }) => {
        const response = isNew ? await createNavigationSubMenu({
            order: newOrder,
            text: title,
            menuId
        }) : await updateNavigationSubMenu({
            text: title,
        }, subMenu.id)
        if (response.data) {
            dispatch(showNotification({
                type: 'success',
                body: _('form.notification.update_success'),
            }))
            onSubmitted && onSubmitted(
                isNew ? (response.data as CreateNavigationSubMenuMutation).createNavigationSubMenu
                    : (response.data as UpdateNavigationSubMenuMutation).updateNavigationSubMenu
            )
            return true
        }
        dispatch(showErrorNotification(response.errors))
        return false
    }, [isNew, newOrder, menuId, subMenu, dispatch, onSubmitted])

    return (
        <Formik
            initialValues={subMenu || { title: '' }}
            validateOnMount
            validationSchema={CreateValidation}
            onSubmit={handleSubmit}
        >
            <CreateNavSubMenuModal close={close} isNew={isNew} />
        </Formik>
    )
}

export default NavSubMenuEditor
